/** Generated multi-line sheets: signature of their single-line source, to detect staleness. */
import type { Id, Project, Sheet } from '@elec/domain';
import { analyse, type Analysis, type CircuitInfo } from './topology.ts';

/**
 * Circuits of the board drawn on a single-line sheet, including those on continuation sheets
 * (same origin RCD or main device, reached through off-page connectors).
 */
export function boardCircuits(analysis: Analysis, fromSheetId: Id): CircuitInfo[] {
  const here = analysis.circuits.filter((c) => c.sheet.id === fromSheetId);
  const origins = new Set(here.map((c) => c.rcds.at(-1)?.id).filter(Boolean));
  return analysis.circuits.filter(
    (c) => c.sheet.id === fromSheetId || (c.rcds.at(-1) && origins.has(c.rcds.at(-1)!.id)),
  );
}

/** The sheet a board starts on (where its origin RCD is), from any of its sheets. */
export function boardRootSheet(project: Project, analysis: Analysis, sheetId: Id): Id {
  const c = analysis.circuits.find((x) => x.sheet.id === sheetId);
  const origin = c?.rcds.at(-1);
  return origin && project.sheets[origin.sheetId]?.kind === 'single-line' ? origin.sheetId : sheetId;
}

/** Stable signature of what the multi-line depends on. */
export function multiLineSignature(analysis: Analysis, fromSheetId: Id, withRefs = false): string {
  const data = boardCircuits(analysis, fromSheetId).map((c) => [
    c.code,
    c.circuit?.label ?? '',
    c.device.tag,
    c.device.symbolId,
    c.device.props.ratingA ?? '',
    c.device.props.curve ?? '',
    c.device.props.poles ?? '',
    c.device.props.phase ?? '',
    c.rcds
      .map(
        (r) =>
          `${r.id}:${r.props.sensitivityMa}:${r.props.rcdType}${withRefs ? `:${r.props.catalogRef ?? ''}` : ''}`,
      )
      .join('>'),
    withRefs ? String(c.device.props.catalogRef ?? '') : '',
    c.cable.type,
    c.cable.cores,
    c.cable.sectionMm2,
  ]);
  const s = JSON.stringify(data);
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

/** Generated sheets that no longer match their single-line source. */
export function staleMultiLines(project: Project, analysis: Analysis = analyse(project)): Sheet[] {
  return Object.values(project.sheets).filter(
    (s) =>
      !!s.source &&
      !!project.sheets[s.source.fromSheetId] &&
      multiLineSignature(analysis, s.source.fromSheetId, s.source.kind === 'board-layout') !==
        s.source.signature,
  );
}
